import { FC, useCallback, useMemo } from "react";
import { useHalls } from "api/hooks";
import ReactGA from "react-ga4";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Routes } from "router";
import { Button, Typography } from "theme";
import { useError } from "utils/hooks";

import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, useTheme } from "@mui/material";

import SubmitButton from "../Submit/SubmitButton.tsx";

import EquipmentGroup from "./components/EquipmentGroup.tsx";
import { EquipmentFormScheme, equipmentScheme } from "./validation.ts";

type EquipmentFormProps = {
  defaultValues: {
    equipment: {
      id: string;
      count: number;
      range: number;
      pricePerUnit: number;
    }[];
  };
};

const EquipmentForm: FC<EquipmentFormProps> = ({ defaultValues }) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { halls } = useHalls();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<EquipmentFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(equipmentScheme),
    defaultValues,
  });

  // TODO: add error handling
  useError(errors);

  const onSubmit: SubmitHandler<EquipmentFormScheme> = useCallback(
    async (data) => {
      if (isValid) {
        const ids = data.equipment.map((el) => el.id).toString();
        ReactGA.event({
          category: "booking_creation",
          action: "equipment_selected",
          label: `${searchParams.toString()}&e=${ids}`,
        });
        localStorage.setItem("equipment", JSON.stringify(data.equipment));
        navigate(
          `${Routes.BookingCreationBookingDetailPreview}?${searchParams.toString()}&e=${ids}`,
        );
      }
    },
    [isValid, navigate, searchParams],
  );

  const equipment = watch("equipment");

  const calculatedPrice = useMemo(() => {
    const selectedHall = halls?.find((h) => String(h.id) === searchParams.get("h"));
    const selectedHour = searchParams.get("t");

    const isNightHour = selectedHour ? selectedHour >= "22:30" || selectedHour < "08:00" : false;

    const hallPrice =
      Number(isNightHour ? selectedHall?.nightPricePerHour : selectedHall?.pricePerHour) *
      Number(searchParams.get("r"));

    const equipmentPrice = equipment.reduce(
      (accumulator, currentValue) =>
        accumulator +
        currentValue.pricePerUnit *
          currentValue.count *
          (currentValue.isFixedPrice ? 1 : currentValue.range > 3 ? 3 : currentValue.range),
      0,
    );

    return isNaN(hallPrice) ? 0 : hallPrice + equipmentPrice;
  }, [halls, searchParams, equipment]);

  return (
    <Box
      component="form"
      autoComplete="off"
      id="equipment-form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      mb={theme.spacing(14)}
    >
      <EquipmentGroup control={control} name="equipment" />
      <SubmitButton
        disabled={equipment.length === 0}
        form="equipment-form"
        skipButton={
          <Button
            type="button"
            onClick={() => {
              localStorage.removeItem("equipment");
              navigate(`${Routes.BookingCreationBookingDetailPreview}?${searchParams.toString()}`);
            }}
            size="large"
            fullWidth
          >
            Пропустити крок
          </Button>
        }
      >
        <Grid2 mb={1} container alignItems="center" justifyContent="space-between">
          <Typography fontSize={18} variant="body1" textTransform="uppercase" weight="bold">
            Загальна сума: {calculatedPrice} ₴
          </Typography>
        </Grid2>
      </SubmitButton>
    </Box>
  );
};

export default EquipmentForm;
